/**
 * Square segment tabs: one bordered strip split into equal parts, the chosen
 * one filled brand green with white words.
 *
 * Used where a screen switches what it shows as a whole — My Orders' kinds,
 * How It Works' two guides — and kept visibly apart from the round filter
 * chips below it, which narrow a list rather than change it.
 */

import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { colors, radius, space } from '../../theme';
import { Text } from './Text';


export interface SegmentedControlProps<T extends string> {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  /** Each option's words, already translated. */
  label: (option: T) => string;
  /** `${prefix}-${option}` on each segment. */
  testIDPrefix: string;
  style?: ViewStyle;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  testIDPrefix,
  style,
}: SegmentedControlProps<T>) {
  return (
    <View style={[styles.strip, style]} accessibilityRole="tablist">
      {options.map((option, index) => {
        const active = option === value;
        return (
          <Pressable
            key={option}
            testID={`${testIDPrefix}-${option}`}
            onPress={() => onChange(option)}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            // The web build reads only aria-* for state.
            aria-selected={active}
            style={({ pressed }) => [
              styles.segment,
              index > 0 && styles.divided,
              active && styles.active,
              pressed && !active && styles.pressed,
            ]}
          >
            <Text variant="label" tone={active ? 'onBrand' : 'secondary'} align="center" numberOfLines={1}>
              {label(option)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  strip: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: colors.border.brand,
    borderRadius: radius.sm,
    overflow: 'hidden',
    backgroundColor: colors.bg.surface,
  },
  segment: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
    paddingVertical: space.sm,
    paddingHorizontal: space.xs,
  },
  divided: {
    borderLeftWidth: 1,
    borderLeftColor: colors.border.brand,
  },
  active: {
    backgroundColor: colors.bg.brand,
  },
  pressed: {
    backgroundColor: colors.bg.brandSubtle,
  },
});
